import type { PlatformConnection } from '../types';
/** Local wall-clock input, with an explicit instant when a DST overlap repeats it. Empty = now. */
export interface BarkTime { local: string; instant?: string }
export const localDate = (date: Date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
export const localDateTime = (date: Date) => `${localDate(date)}T${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;

/** Enumerate real instants for a device-local minute; gaps return none, overlaps return both. */
export function localTimeCandidates(value: string): string[] {
  if (!/^\d{4}-\d\d-\d\dT\d\d:\d\d$/.test(value)) return [];
  const nominal = Date.parse(`${value}:00Z`);
  if (!Number.isFinite(nominal)) return [];
  const offsets = new Set<number>();
  for (let hour = -36; hour <= 36; hour += 6) offsets.add(new Date(nominal + hour * 3600000).getTimezoneOffset());
  return [...offsets].map((offset) => new Date(nominal + offset * 60000))
    .filter((date) => localDateTime(date) === value).map((date) => date.toISOString()).sort();
}
export function resolveBarkTime(value: BarkTime, now = Date.now()): string | null {
  if (!value.local) return null;
  const candidates = localTimeCandidates(value.local);
  if (!candidates.length) throw new Error('This local time does not exist. Choose a valid date and time.');
  if (candidates.length > 1 && !candidates.includes(value.instant ?? '')) throw new Error('This time occurs twice. Choose its UTC offset.');
  const instant = candidates.length > 1 ? value.instant! : candidates[0]!;
  if (Date.parse(instant) <= now) throw new Error('Choose a future date and time.');
  return instant;
}
export function seedBarkTime(day?: string, now = Date.now()): BarkTime {
  if (!day) return { local: '' };
  const soon = new Date(Math.ceil((now + 30 * 60000) / 60000) * 60000);
  return { local: day <= localDate(soon) ? localDateTime(soon) : `${day}T09:00` };
}

/** The one label a person reads for a connection row; the wire has no `name`. */
export const connectionLabel = (connection: Pick<PlatformConnection, 'account_name' | 'platform_display_name' | 'platform'>) =>
  connection.account_name || connection.platform_display_name || connection.platform;
